import { Box } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";

export type Award =
  | "presidential"
  | "national"
  | "participation"
  | "incomplete"
  | "pending"
  | "absent"
  | "excused";

export interface AwardChipProps {
  award: Award;
  size?: "sm" | "md";
  /** Show the tier artwork from `/assets/tiers/` (presidential, national, participation only). */
  showIcon?: boolean;
}

const ICON: Partial<Record<Award, string>> = {
  presidential: "/assets/tiers/presidential.svg",
  national: "/assets/tiers/national.svg",
  participation: "/assets/tiers/participation.svg",
};

const colorsFor = (theme: Theme, award: Award) => {
  const p = theme.palette;
  switch (award) {
    case "presidential":
      return { bg: p.tierPresidential, fg: p.onTier, line: "transparent" };
    case "national":
      return { bg: p.tierNational, fg: p.onTier, line: "transparent" };
    case "participation":
      return {
        bg: p.tierParticipationBg,
        fg: p.tierParticipationFg,
        line: "transparent",
      };
    case "incomplete":
      return { bg: p.warning.light, fg: p.warning.main, line: "transparent" };
    case "pending":
      return { bg: p.surfaceAlt, fg: p.text.secondary, line: "transparent" };
    default:
      return { bg: "transparent", fg: p.text.secondary, line: p.divider };
  }
};

/** Tier badge. The text label is always present; the star and artwork are decorative. */
export const AwardChip = ({
  award,
  size = "md",
  showIcon = false,
}: AwardChipProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { bg, fg, line } = colorsFor(theme, award);

  const labels: Record<Award, string> = {
    presidential: t("awards.presidential", { defaultValue: "Presidential" }),
    national: t("awards.national", { defaultValue: "National" }),
    participation: t("awards.participation", { defaultValue: "Participation" }),
    incomplete: t("awards.incomplete", { defaultValue: "Incomplete" }),
    pending: t("awards.pending", { defaultValue: "In progress" }),
    absent: t("awards.absent", { defaultValue: "Absent" }),
    excused: t("awards.excused", { defaultValue: "Excused" }),
  };
  const icon = showIcon ? ICON[award] : undefined;
  const small = size === "sm";

  return (
    <Box
      component="span"
      data-award={award}
      data-size={size}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        px: small ? "10px" : "11px",
        py: "7px",
        borderRadius: `${radius.pill}px`,
        bgcolor: bg,
        color: fg,
        boxShadow: line === "transparent" ? "none" : `inset 0 0 0 1px ${line}`,
        // Design `AW` chips: 800 10 px, font-stretch 110%, no tracking.
        fontSize: small ? 10 : 10.5,
        fontWeight: 800,
        lineHeight: 1,
        textTransform: "uppercase",
        fontStretch: "110%",
        whiteSpace: "nowrap",
      }}
    >
      {icon && (
        <Box
          component="img"
          src={icon}
          alt=""
          aria-hidden="true"
          sx={{ width: small ? 14 : 18, height: small ? 14 : 18 }}
        />
      )}
      {award === "presidential" && <span aria-hidden="true">★</span>}
      {labels[award]}
    </Box>
  );
};

export default AwardChip;
